import {
  AMT_UI_SCALE,
  D,
  calcProfit,
  fmtAmt,
  type CcyCode,
  type Money,
  type ProfitInput,
} from '@wave/shared';

/**
 * Дерево разбора арифметики («как получилась цифра») для FormulaHint.
 *
 * Инвариант «подсказка не может врать»: builders НЕ принимают готовый результат —
 * они сами считают его теми же shared-функциями (calcProfit, D), что и реальный
 * расчёт. Тесты (breakdown.test.ts) фиксируют равенство «итог дерева == итог
 * боевого расчёта».
 *
 * ВАЖНО ПРИ РАСШИРЕНИИ МОДЕЛИ ЗАЯВКИ: новое поле, влияющее на суммы/профит
 * (наценка, комиссия, скидка, ...), обязано появиться и здесь — в терминах
 * соответствующих разборов. Иначе менеджер увидит формулу, в которой «не хватает»
 * слагаемого, и перестанет доверять подсказке. См. CLAUDE.md.
 */

export type BreakdownColor = 'g' | 'r' | 'a' | 'v' | 'b';
export type OriginBadge = 'ввод' | 'снапшот' | 'настройка' | 'вычислено' | 'рынок';

/** Токен формульной строки: операнд (кликабельный, связан цветом) или оператор. */
export interface FToken {
  text: string;
  /** Ключ связки hover: формула ↔ строка чека ↔ поле формы. Нет у операторов. */
  k?: string;
  color?: BreakdownColor;
}

export interface BreakdownSub {
  /** Мини-формула вложенного расчёта (уже с числами). */
  numbers: FToken[];
  note?: string;
}

/** Строка «чека»-легенды: операнд, его происхождение и значение. */
export interface BreakdownTerm {
  k: string;
  color: BreakdownColor;
  label: string;
  badge: OriginBadge;
  /** Уточнение к бейджу, напр. «рынок +0.96%». */
  badgeNote?: string;
  value: string;
  /** data-fh идентификатор поля формы для подсветки и клика-фокуса. */
  fieldRef?: string;
  sub?: BreakdownSub;
}

export interface BreakdownCheck {
  level: 'ok' | 'warn' | 'bad';
  text: string;
  action?: { id: 'flip-cl' | 'flip-ex'; label: string };
}

export interface BreakdownFormula {
  title: string;
  /** Формула словами. Пустой массив = не показывать строку слов. */
  words: FToken[];
  /** Формула с подставленными числами (без результата). */
  numbers: FToken[];
  result: { display: string; tone: 'pos' | 'neg' | 'neu' };
  /** Строка округления: показывается когда округление реально что-то срезало. */
  round?: { exact: string; note: string } | null;
  terms: BreakdownTerm[];
  notes?: string[];
  checks?: BreakdownCheck[];
}

const op = (text: string): FToken => ({ text });
const tk = (k: string, color: BreakdownColor, text: string): FToken => ({ k, color, text });

/** Направление отображения курса в форме (RateDir из NewOrderModal). */
export type RateDisplayDir = 'fwd' | 'inv';

/**
 * Бейдж «рынок ±x%» к курсу — В ОТОБРАЖАЕМОМ НАПРАВЛЕНИИ котировки.
 * Дельта в обратной котировке меняет знак ((1/r)/(1/m) = m/r), поэтому
 * сравнивать канонические значения при inv-отображении нельзя — разбор
 * показал бы «+1%» там, где под полем написано «−1%».
 */
export function rateMarketNote(
  rate: Money | null | undefined,
  market: Money | null | undefined,
  dir: RateDisplayDir = 'fwd',
): string | null {
  if (!rate || !market) return null;
  const r = D(rate);
  const m = D(market);
  if (r.lte(0) || m.lte(0)) return null;
  const pct =
    dir === 'inv'
      ? m.dividedBy(r).minus(1).times(100) // (1/r)/(1/m) − 1
      : r.dividedBy(m).minus(1).times(100);
  return pct.abs().lt('0.005') ? 'рынок' : `рынок ${pct.gte(0) ? '+' : ''}${pct.toFixed(2)}%`;
}

/** Курс в отображаемом направлении: канонический или его инверсия (8 знаков). */
function displayRate(canonical: ReturnType<typeof D>, dir: RateDisplayDir) {
  return dir === 'inv' ? D(1).dividedBy(canonical).toDecimalPlaces(8) : canonical.toDecimalPlaces(8);
}

/** «Точное → показанное» — только если округление реально изменило число.
 *  Для показа exact режем до 8 знаков (полный хвост деления нечитаем). */
function roundInfo(exact: ReturnType<typeof D>, shown: string, note: string) {
  return exact.toString() !== D(shown).toString()
    ? { exact: exact.toDecimalPlaces(8).toString(), note }
    : null;
}

const fmtUsdt = (v: ReturnType<typeof D>) => v.toFixed(2);

// ────────────────────────────────────────────────────────────────────────────
// Автопересчёт «2 из 3» (клиентская пара give/get/rate)
// ────────────────────────────────────────────────────────────────────────────

export interface PairAmountArgs {
  /** Что вычислено: get = give × rate ИЛИ give = get ÷ rate (канонически). */
  mode: 'get-from-give' | 'give-from-get';
  giveAmt: Money;
  getAmt: Money;
  /** Канонический курс формы: сколько get за 1 give. */
  rate: Money;
  giveCcy: CcyCode;
  getCcy: CcyCode;
  /**
   * Направление ОТОБРАЖЕНИЯ курса в поле (rateDir формы). Разбор показывает
   * курс ровно в той котировке, что видит менеджер: при 'inv' курс
   * инвертируется, а операция переворачивается (× ↔ ÷). Иначе разбор путает.
   */
  rateDir?: RateDisplayDir;
  /** Канонический рыночный курс — для бейджа «рынок ±x%» (в направлении показа). */
  marketRate?: Money | null;
  fieldRefs?: { give?: string; rate?: string; get?: string };
}

/** Разбор автопересчитанной суммы клиентской пары. */
export function bdPairAmount(a: PairAmountArgs): BreakdownFormula {
  const give = D(a.giveAmt || '0');
  const get = D(a.getAmt || '0');
  const rate = D(a.rate || '0');
  const dir: RateDisplayDir = a.rateDir ?? 'fwd';
  const isGet = a.mode === 'get-from-give';
  // Точный результат — всегда по каноническому курсу (как считает форма).
  const exact = isGet ? give.times(rate) : get.dividedBy(rate);
  const shown = exact.toFixed(AMT_UI_SCALE);
  const resCcy = isGet ? a.getCcy : a.giveCcy;

  // В отображаемой котировке: fwd — get за 1 give (как канонический),
  // inv — give за 1 get (курс инвертирован, операция перевёрнута).
  const disp = displayRate(rate, dir);
  const rateQuote = dir === 'inv' ? `${a.giveCcy} за 1 ${a.getCcy}` : `${a.getCcy} за 1 ${a.giveCcy}`;
  const opText = (isGet ? dir === 'fwd' : dir === 'inv') ? '×' : '÷';

  const srcTok = isGet
    ? tk('src', 'g', fmtAmt(give.toString(), a.giveCcy))
    : tk('src', 'g', fmtAmt(get.toString(), a.getCcy));
  const rateTok = tk('rate', 'v', disp.toString());
  const note = rateMarketNote(a.rate, a.marketRate, dir);

  return {
    title: 'Как получилась сумма',
    words: [
      tk('src', 'g', isGet ? 'отдаёт нам' : 'получает'),
      op(opText),
      tk('rate', 'v', 'курс'),
    ],
    numbers: [srcTok, op(opText), rateTok],
    result: { display: `${fmtAmt(shown, resCcy)}`, tone: 'neu' },
    round: roundInfo(exact, shown, `округление до ${AMT_UI_SCALE} знаков (суммы в UI)`),
    terms: [
      {
        k: 'src',
        color: 'g',
        label: isGet ? '«Отдаёт нам»' : '«Получает от нас»',
        badge: 'ввод',
        value: isGet ? fmtAmt(give.toString(), a.giveCcy) : fmtAmt(get.toString(), a.getCcy),
        fieldRef: isGet ? a.fieldRefs?.give : a.fieldRefs?.get,
      },
      {
        k: 'rate',
        color: 'v',
        label: `Курс для клиента (${rateQuote})`,
        badge: 'ввод',
        badgeNote: note ?? undefined,
        value: disp.toString(),
        fieldRef: a.fieldRefs?.rate,
      },
    ],
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Сумма исполнителю (авто): exGet = clGet ÷ exRate
// ────────────────────────────────────────────────────────────────────────────

export interface ExecAutoArgs {
  clGetAmt: Money;
  /** Канонический курс исполнителя: сколько clGetCcy за 1 exGetCcy. */
  exRate: Money;
  clGetCcy: CcyCode;
  exGetCcy: CcyCode;
  /** Направление отображения курса в поле (см. PairAmountArgs.rateDir). */
  rateDir?: RateDisplayDir;
  marketRate?: Money | null;
  fieldRefs?: { clGet?: string; exRate?: string };
}

export function bdExecAuto(a: ExecAutoArgs): BreakdownFormula {
  const get = D(a.clGetAmt || '0');
  const rate = D(a.exRate || '0');
  const dir: RateDisplayDir = a.rateDir ?? 'fwd';
  // Канонически: exGet = clGet ÷ exRate. В inv-отображении курс инвертирован
  // (exGetCcy за 1 clGetCcy) → операция переворачивается: exGet = clGet × disp.
  const exact = get.dividedBy(rate);
  const shown = exact.toFixed(AMT_UI_SCALE);
  const disp = displayRate(rate, dir);
  const rateQuote = dir === 'inv' ? `${a.exGetCcy} за 1 ${a.clGetCcy}` : `${a.clGetCcy} за 1 ${a.exGetCcy}`;
  const opText = dir === 'inv' ? '×' : '÷';
  const note = rateMarketNote(a.exRate, a.marketRate, dir);
  return {
    title: 'Как получилась сумма исполнителю',
    words: [tk('get', 'g', 'клиент получает'), op(opText), tk('rate', 'v', 'курс исполнителя')],
    numbers: [
      tk('get', 'g', fmtAmt(get.toString(), a.clGetCcy)),
      op(opText),
      tk('rate', 'v', disp.toString()),
    ],
    result: { display: fmtAmt(shown, a.exGetCcy), tone: 'neu' },
    round: roundInfo(exact, shown, `округление до ${AMT_UI_SCALE} знаков (суммы в UI)`),
    terms: [
      {
        k: 'get',
        color: 'g',
        label: '«Получает от нас» (сумма клиенту)',
        badge: 'ввод',
        value: fmtAmt(get.toString(), a.clGetCcy),
        fieldRef: a.fieldRefs?.clGet,
      },
      {
        k: 'rate',
        color: 'v',
        label: `Курс исполнителя (${rateQuote})`,
        badge: 'ввод',
        badgeNote: note ?? undefined,
        value: disp.toString(),
        fieldRef: a.fieldRefs?.exRate,
      },
    ],
    notes: ['Фикс и бонус (если заданы) прибавляются к этой сумме отдельно — см. карточку «Платим исполнителю».'],
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Итог клиента: база × (1 + коэф) + фикс
// ────────────────────────────────────────────────────────────────────────────

export interface ClientTotalArgs {
  baseAmt: Money;
  /** Процент коэффициента ('' / null = нет). */
  coefPct?: Money | null;
  /** Фикс в USDT и курс конвертации в валюту клиента (null = нет фикса). */
  fixUsdt?: Money | null;
  /** Курьер в USDT — ТОЛЬКО режим 'extra' (клиент доплачивает). null = нет/included. */
  courierUsdt?: Money | null;
  rateForCcy?: Money | null;
  ccy: CcyCode;
  /** Откуда курс конвертации: снапшот (карточка) или справочник/фикс (форма). */
  rateBadge?: OriginBadge;
  fieldRefs?: { base?: string; coef?: string; fix?: string; courier?: string };
}

/** Разбор финальной суммы клиента («Получаем от клиента»). */
export function bdClientTotal(a: ClientTotalArgs): BreakdownFormula {
  const base = D(a.baseAmt || '0');
  const pct = a.coefPct && !D(a.coefPct).isZero() ? D(a.coefPct) : null;
  const k = pct ? D(1).plus(pct.dividedBy(100)) : D(1);
  const afterCoef = base.times(k);
  const fixUsdt = a.fixUsdt && D(a.fixUsdt).gt(0) && a.rateForCcy ? D(a.fixUsdt) : null;
  const fixAmt = fixUsdt ? fixUsdt.times(D(a.rateForCcy!)) : D(0);
  const courUsdt = a.courierUsdt && D(a.courierUsdt).gt(0) && a.rateForCcy ? D(a.courierUsdt) : null;
  const courAmt = courUsdt ? courUsdt.times(D(a.rateForCcy!)) : D(0);
  const exact = afterCoef.plus(fixAmt).plus(courAmt);
  const shown = exact.toFixed(AMT_UI_SCALE);

  const words: FToken[] = [tk('base', 'g', 'база')];
  const numbers: FToken[] = [tk('base', 'g', fmtAmt(base.toString(), a.ccy))];
  if (pct) {
    words.push(op('×'), tk('coef', 'a', '(1 + коэф)'));
    numbers.push(op('×'), tk('coef', 'a', k.toString()));
  }
  if (fixUsdt) {
    words.push(op('+'), tk('fix', 'b', 'фикс'));
    numbers.push(op('+'), tk('fix', 'b', fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)));
  }
  if (courUsdt) {
    words.push(op('+'), tk('cour', 'r', 'курьер'));
    numbers.push(op('+'), tk('cour', 'r', fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)));
  }

  const terms: BreakdownTerm[] = [
    {
      k: 'base',
      color: 'g',
      label: 'База — введённая сумма клиента',
      badge: 'ввод',
      value: fmtAmt(base.toString(), a.ccy),
      fieldRef: a.fieldRefs?.base,
    },
  ];
  if (pct) {
    terms.push({
      k: 'coef',
      color: 'a',
      label: `Коэффициент ${pct.gt(0) ? '+' : ''}${pct.toString()}% → наценка (транзит рефереру)`,
      badge: 'настройка',
      value: `${afterCoef.minus(base).gte(0) ? '+' : ''}${fmtAmt(afterCoef.minus(base).toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.coef,
    });
  }
  if (fixUsdt) {
    terms.push({
      k: 'fix',
      color: 'b',
      label: 'Фикс за платёжку (транзитом исполнителю 1:1)',
      badge: 'вычислено',
      value: `+${fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.fix,
      sub: {
        numbers: [
          tk('fixu', 'b', `${fixUsdt.toString()} USDT`),
          op('×'),
          tk('rate', 'v', D(a.rateForCcy!).toString()),
          op('='),
          tk('fix', 'b', fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)),
        ],
        note: `поле «Фикс за платёжку» × курс ${a.ccy}→USDT (${a.rateBadge ?? 'справочник'})`,
      },
    });
  }
  if (courUsdt) {
    terms.push({
      k: 'cour',
      color: 'r',
      label: 'Курьер «сверх» — доплата клиента (целиком исполнителю)',
      badge: 'вычислено',
      value: `+${fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.courier,
      sub: {
        numbers: [
          tk('couru', 'r', `${courUsdt.toString()} USDT`),
          op('×'),
          tk('rate', 'v', D(a.rateForCcy!).toString()),
          op('='),
          tk('cour', 'r', fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)),
        ],
        note: 'транзитом: исполнителю уйдёт столько же — профит не меняется',
      },
    });
  }

  return {
    title: 'Как получилась сумма клиента',
    words,
    numbers,
    result: { display: fmtAmt(shown, a.ccy), tone: 'neu' },
    round: roundInfo(exact, shown, `округление до ${AMT_UI_SCALE} знаков`),
    terms,
    notes: pct
      ? ['Порядок фиксирован: сначала коэффициент к базе, потом фикс. Профит считается от базы.']
      : undefined,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Итог исполнителя: обмен + фикс + бонус
// ────────────────────────────────────────────────────────────────────────────

export interface ExecutorTotalArgs {
  exchangeAmt: Money;
  fixUsdt?: Money | null;
  bonusUsdt?: Money | null;
  /** Курьер в USDT — ТОЛЬКО режим 'extra' (влит в оплату). null = нет/included. */
  courierUsdt?: Money | null;
  /** Курьер 'included': в оплату не влит — добавляем поясняющий терм-примечание. */
  courierIncludedUsdt?: Money | null;
  rateForCcy?: Money | null;
  ccy: CcyCode;
  rateBadge?: OriginBadge;
  fieldRefs?: { exchange?: string; fix?: string; bonus?: string; courier?: string };
}

/** Разбор финальной суммы исполнителю («Платим исполнителю»). */
export function bdExecutorTotal(a: ExecutorTotalArgs): BreakdownFormula {
  const exch = D(a.exchangeAmt || '0');
  const rate = a.rateForCcy ? D(a.rateForCcy) : null;
  const fixU = a.fixUsdt && D(a.fixUsdt).gt(0) && rate ? D(a.fixUsdt) : null;
  const bonU = a.bonusUsdt && D(a.bonusUsdt).gt(0) && rate ? D(a.bonusUsdt) : null;
  const courU = a.courierUsdt && D(a.courierUsdt).gt(0) && rate ? D(a.courierUsdt) : null;
  const fixAmt = fixU ? fixU.times(rate!) : D(0);
  const bonAmt = bonU ? bonU.times(rate!) : D(0);
  const courAmt = courU ? courU.times(rate!) : D(0);
  const exact = exch.plus(fixAmt).plus(bonAmt).plus(courAmt);
  const shown = exact.toFixed(AMT_UI_SCALE);

  const words: FToken[] = [tk('exch', 'r', 'обмен')];
  const numbers: FToken[] = [tk('exch', 'r', fmtAmt(exch.toString(), a.ccy))];
  if (fixU) {
    words.push(op('+'), tk('fix', 'b', 'фикс'));
    numbers.push(op('+'), tk('fix', 'b', fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)));
  }
  if (bonU) {
    words.push(op('+'), tk('bon', 'a', 'бонус'));
    numbers.push(op('+'), tk('bon', 'a', fmtAmt(bonAmt.toDecimalPlaces(4).toString(), a.ccy)));
  }
  if (courU) {
    words.push(op('+'), tk('cour', 'r', 'курьер'));
    numbers.push(op('+'), tk('cour', 'r', fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)));
  }

  const terms: BreakdownTerm[] = [
    {
      k: 'exch',
      color: 'r',
      label: 'Обмен — за отправку суммы клиенту',
      badge: 'ввод',
      value: fmtAmt(exch.toString(), a.ccy),
      fieldRef: a.fieldRefs?.exchange,
    },
  ];
  if (fixU) {
    terms.push({
      k: 'fix',
      color: 'b',
      label: 'Фикс за платёжку (транзитом от клиента 1:1)',
      badge: 'вычислено',
      value: `+${fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.fix,
      sub: {
        numbers: [
          tk('fixu', 'b', `${fixU.toString()} USDT`),
          op('×'),
          tk('rate', 'v', rate!.toString()),
          op('='),
          tk('fix', 'b', fmtAmt(fixAmt.toDecimalPlaces(4).toString(), a.ccy)),
        ],
        note: 'взяли с клиента столько же — профит не меняется',
      },
    });
  }
  if (bonU) {
    terms.push({
      k: 'bon',
      color: 'a',
      label: 'Бонус исполнителю (из маржи компании)',
      badge: 'вычислено',
      value: `+${fmtAmt(bonAmt.toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.bonus,
      sub: {
        numbers: [
          tk('bonu', 'a', `${bonU.toString()} USDT`),
          op('×'),
          tk('rate', 'v', rate!.toString()),
          op('='),
          tk('bon', 'a', fmtAmt(bonAmt.toDecimalPlaces(4).toString(), a.ccy)),
        ],
        note: 'клиента не касается — вычитается из профита (нетто)',
      },
    });
  }
  if (courU) {
    terms.push({
      k: 'cour',
      color: 'r',
      label: 'Курьер «сверх» (доплачен клиентом 1:1)',
      badge: 'вычислено',
      value: `+${fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)}`,
      fieldRef: a.fieldRefs?.courier,
      sub: {
        numbers: [
          tk('couru', 'r', `${courU.toString()} USDT`),
          op('×'),
          tk('rate', 'v', rate!.toString()),
          op('='),
          tk('cour', 'r', fmtAmt(courAmt.toDecimalPlaces(4).toString(), a.ccy)),
        ],
        note: 'транзитом: взяли с клиента столько же — профит не меняется',
      },
    });
  }

  const notes = ['Фикс, бонус и курьер-«сверх» «влиты» в ногу исполнителя — отдельных проводок для них нет.'];
  if (a.courierIncludedUsdt && D(a.courierIncludedUsdt).gt(0)) {
    notes.push(
      `Курьер ${D(a.courierIncludedUsdt).toString()} USDT «из суммы» в эту оплату НЕ влит — исполнитель удерживает его из выдачи клиенту (клиент получает меньше).`,
    );
  }

  return {
    title: 'Как получилась сумма исполнителю',
    words,
    numbers,
    result: { display: fmtAmt(shown, a.ccy), tone: 'neu' },
    round: roundInfo(exact, shown, `округление до ${AMT_UI_SCALE} знаков`),
    terms,
    notes,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Профит (universal-USDT): приход − расход − бонус
// ────────────────────────────────────────────────────────────────────────────

export interface ProfitBreakdownArgs {
  /** Тот же вход, что у боевого calcProfit (clGiveAmt = БАЗА, exGetAmt = ОБМЕН). */
  input: ProfitInput;
  /** Бейдж происхождения курсов: «снапшот» (карточка) / «настройка» (форма-фиксы). */
  ratesBadge: OriginBadge;
  fieldRefs?: { base?: string; exchange?: string; bonus?: string };
  checks?: BreakdownCheck[];
}

/** Разбор профита. Итог считает БОЕВОЙ calcProfit — расхождение исключено. */
export function bdProfit(a: ProfitBreakdownArgs): BreakdownFormula {
  const r = calcProfit(a.input);
  const receive = D(r.receiveUsdt);
  const pay = D(r.payUsdt);
  const bonus = a.input.bonusUsdt && !D(a.input.bonusUsdt).isZero() ? D(a.input.bonusUsdt) : null;
  const profit = D(r.amount);
  const tone: 'pos' | 'neg' | 'neu' = profit.abs().lt('0.005') ? 'neu' : profit.isNegative() ? 'neg' : 'pos';

  const giveIsUsdt = a.input.clGiveCcy === 'USDT';
  const payIsUsdt = a.input.exGetCcy === 'USDT';
  const giveRate = a.input.rates[a.input.clGiveCcy];
  const payRate = a.input.rates[a.input.exGetCcy];
  const sameRate = a.input.clGiveCcy === a.input.exGetCcy;

  const words: FToken[] = [tk('in', 'g', 'приход'), op('−'), tk('out', 'r', 'расход')];
  const numbers: FToken[] = [tk('in', 'g', fmtUsdt(receive)), op('−'), tk('out', 'r', fmtUsdt(pay))];
  if (bonus) {
    words.push(op('−'), tk('bon', 'a', 'бонус'));
    numbers.push(op('−'), tk('bon', 'a', fmtUsdt(bonus)));
  }

  const terms: BreakdownTerm[] = [
    {
      k: 'in',
      color: 'g',
      label: 'Приход от клиента в USDT',
      badge: 'вычислено',
      value: fmtUsdt(receive),
      sub: giveIsUsdt
        ? { numbers: [tk('in', 'g', `${fmtAmt(a.input.clGiveAmt, 'USDT')} — уже в USDT`)], note: 'база клиента без коэффициента (наценка — транзит, в профит не входит)' }
        : {
            numbers: [
              tk('base', 'g', fmtAmt(a.input.clGiveAmt, a.input.clGiveCcy)),
              op('÷'),
              tk(sameRate ? 'rate' : 'grate', 'v', D(giveRate ?? '0').toString()),
              op('='),
              tk('in', 'g', fmtUsdt(receive)),
            ],
            note: 'база клиента без коэффициента и фикса (они — транзит, в профит не входят)',
          },
    },
    {
      k: 'out',
      color: 'r',
      label: 'Расход на исполнителя в USDT',
      badge: 'вычислено',
      value: fmtUsdt(pay),
      sub: payIsUsdt
        ? { numbers: [tk('out', 'r', `${fmtAmt(a.input.exGetAmt, 'USDT')} — уже в USDT`)], note: 'обмен без фикса и бонуса' }
        : {
            numbers: [
              tk('exch', 'r', fmtAmt(a.input.exGetAmt, a.input.exGetCcy)),
              op('÷'),
              tk(sameRate ? 'rate' : 'prate', 'v', D(payRate ?? '0').toString()),
              op('='),
              tk('out', 'r', fmtUsdt(pay)),
            ],
            note: 'обмен без фикса (он транзитом) и бонуса',
          },
    },
  ];
  if (bonus) {
    terms.push({
      k: 'bon',
      color: 'a',
      label: 'Бонус исполнителю (из маржи)',
      badge: 'настройка',
      value: fmtUsdt(bonus),
      fieldRef: a.fieldRefs?.bonus,
    });
  }
  if (sameRate && !giveIsUsdt) {
    terms.push({
      k: 'rate',
      color: 'v',
      label: `Курс ${a.input.clGiveCcy}→USDT`,
      badge: a.ratesBadge,
      value: D(giveRate ?? '0').toString(),
    });
  } else {
    if (!giveIsUsdt) {
      terms.push({
        k: 'grate',
        color: 'v',
        label: `Курс ${a.input.clGiveCcy}→USDT`,
        badge: a.ratesBadge,
        value: D(giveRate ?? '0').toString(),
      });
    }
    if (!payIsUsdt) {
      terms.push({
        k: 'prate',
        color: 'v',
        label: `Курс ${a.input.exGetCcy}→USDT`,
        badge: a.ratesBadge,
        value: D(payRate ?? '0').toString(),
      });
    }
  }

  const native =
    r.nativeAmount && r.nativeCcy && !D(r.nativeAmount).isZero()
      ? `Точно в валюте: ${D(r.nativeAmount).gte(0) ? '+' : ''}${fmtAmt(r.nativeAmount, r.nativeCcy)} (same-ccy, без зависимости от курсов)`
      : null;

  return {
    title: 'Как получился профит',
    words,
    numbers,
    result: {
      display: `${profit.gte(0) ? '+' : ''}${fmtUsdt(profit)} USDT`,
      tone,
    },
    round: null,
    terms,
    notes: [
      ...(native ? [native] : []),
      a.ratesBadge === 'снапшот'
        ? 'Курсы зафиксированы при создании заявки — профит не «поплывёт» от смены курсов в справочнике.'
        : 'При создании заявки эти курсы зафиксируются в снапшоте.',
    ],
    checks: a.checks,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Маржа и объём/расход (карточка заявки)
// ────────────────────────────────────────────────────────────────────────────

export function bdMargin(args: {
  profitUsdt: Money;
  baseUsdt: Money;
  profitSub?: BreakdownSub;
  volumeSub?: BreakdownSub;
}): BreakdownFormula {
  const profit = D(args.profitUsdt);
  const vol = D(args.baseUsdt);
  const exact = vol.gt(0) ? profit.dividedBy(vol).times(100) : D(0);
  const shown = exact.toFixed(2);
  return {
    title: 'Как получилась маржа',
    words: [tk('pr', 'g', 'профит'), op('÷'), tk('vol', 'b', 'объём базы'), op('× 100')],
    numbers: [tk('pr', 'g', fmtUsdt(profit)), op('÷'), tk('vol', 'b', fmtUsdt(vol)), op('× 100')],
    result: { display: `${exact.gte(0) ? '+' : ''}${shown}%`, tone: exact.isNegative() ? 'neg' : 'pos' },
    round: roundInfo(exact, shown, 'округление до 2 знаков'),
    terms: [
      {
        k: 'pr',
        color: 'g',
        label: 'Чистый профит, USDT',
        badge: 'вычислено',
        value: fmtUsdt(profit),
        sub: args.profitSub,
      },
      {
        k: 'vol',
        color: 'b',
        label: 'Объём базы в USDT',
        badge: 'вычислено',
        value: fmtUsdt(vol),
        sub: args.volumeSub,
      },
    ],
    notes: ['Маржа считается от базы (той части, что участвует в профите), а не от полной суммы с коэффициентом.'],
  };
}

/** Объём («получили от клиента») или Расход («отдали исполнителю») в USDT. */
export function bdUsdtEquivalent(args: {
  kind: 'volume' | 'expense';
  amt: Money;
  ccy: CcyCode;
  rate: Money;
  ratesBadge: OriginBadge;
  note?: string;
}): BreakdownFormula {
  const amt = D(args.amt);
  const isUsdt = args.ccy === 'USDT';
  const exact = isUsdt ? amt : amt.dividedBy(D(args.rate));
  const shown = exact.toFixed(2);
  const label = args.kind === 'volume' ? 'Получили от клиента' : 'Отдали исполнителю';
  const color: BreakdownColor = args.kind === 'volume' ? 'g' : 'r';
  return {
    title: args.kind === 'volume' ? 'Как получился объём' : 'Как получился расход',
    words: isUsdt ? [] : [tk('amt', color, label.toLowerCase()), op('÷'), tk('rate', 'v', 'курс')],
    numbers: isUsdt
      ? [tk('amt', color, `${fmtAmt(amt.toString(), 'USDT')} — уже в USDT`)]
      : [tk('amt', color, fmtAmt(amt.toString(), args.ccy)), op('÷'), tk('rate', 'v', D(args.rate).toString())],
    result: { display: `${shown} USDT`, tone: 'neu' },
    round: roundInfo(exact, shown, 'округление до 2 знаков'),
    terms: [
      {
        k: 'amt',
        color,
        label,
        badge: 'ввод',
        value: fmtAmt(amt.toString(), args.ccy),
      },
      ...(isUsdt
        ? []
        : [
            {
              k: 'rate',
              color: 'v' as const,
              label: `Курс ${args.ccy}→USDT`,
              badge: args.ratesBadge,
              value: D(args.rate).toString(),
            },
          ]),
    ],
    notes: args.note ? [args.note] : undefined,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Активные проверки
// ────────────────────────────────────────────────────────────────────────────

/** Порог «курс подозрительно далеко от рынка», % (по модулю). */
export const RATE_MARKET_WARN_PCT = '2';
/** ε безубытка — как в превью формы (шум округления, не реальный убыток). */
export const PROFIT_NEAR_ZERO_EPS = '0.005';
/** Допуск распознавания обратной котировки: |rate × market − 1| < 2%. */
const INVERSE_MATCH_EPS = '0.02';

export interface RateCheckArgs {
  target: 'cl' | 'ex';
  label: string;
  rate?: Money | null;
  market?: Money | null;
}

/** Проверки одного курса против рынка: отклонение и «перепутано направление». */
export function buildRateChecks(a: RateCheckArgs): BreakdownCheck[] {
  if (!a.rate || !a.market) return [];
  const rate = D(a.rate);
  const market = D(a.market);
  if (rate.lte(0) || market.lte(0)) return [];
  const out: BreakdownCheck[] = [];

  // Обратная котировка: rate ≈ 1/market → rate×market ≈ 1 (но не для пар,
  // где сам рынок ≈ 1 — там прямая и обратная неразличимы).
  const inv = rate.times(market).minus(1).abs();
  const marketNearOne = market.minus(1).abs().lt('0.05');
  if (!marketNearOne && inv.lt(INVERSE_MATCH_EPS)) {
    out.push({
      level: 'warn',
      text: `${a.label} ${rate.toDecimalPlaces(8)} — это ≈ 1 ÷ ${market.toDecimalPlaces(4)}, обратная котировка рынка. Похоже, перепутано направление.`,
      action: { id: a.target === 'cl' ? 'flip-cl' : 'flip-ex', label: '⇄ Перевернуть курс и пересчитать' },
    });
    return out; // отклонение при этом заведомо огромное — не дублируем
  }

  const devPct = rate.minus(market).dividedBy(market).times(100);
  if (devPct.abs().gt(RATE_MARKET_WARN_PCT)) {
    out.push({
      level: 'warn',
      text: `${a.label} отклонён от рынка на ${devPct.gte(0) ? '+' : ''}${devPct.toFixed(2)}% (порог ±${RATE_MARKET_WARN_PCT}%)`,
    });
  }
  return out;
}

/** Сводные проверки профита: знак/ноль + курсы; при отсутствии проблем — ok-строка. */
export function buildProfitChecks(a: {
  profitUsdt: Money;
  marginPct?: Money | null;
  rateChecks: BreakdownCheck[];
}): BreakdownCheck[] {
  const profit = D(a.profitUsdt);
  const out: BreakdownCheck[] = [];
  const nearZero = profit.abs().lt(PROFIT_NEAR_ZERO_EPS);

  if (!nearZero && profit.isNegative()) {
    out.push({
      level: 'bad',
      text: `Профит отрицательный: сделка в минус на ${profit.abs().toFixed(2)} USDT${a.marginPct ? ` (маржа ${D(a.marginPct).toFixed(2)}%)` : ''}`,
    });
  }
  out.push(...a.rateChecks);

  if (out.length === 0) {
    const marginTxt = a.marginPct ? ` · маржа ${D(a.marginPct).gte(0) ? '+' : ''}${D(a.marginPct).toFixed(2)}%` : '';
    out.push({
      level: 'ok',
      text: nearZero
        ? `Безубыток (|профит| < ${PROFIT_NEAR_ZERO_EPS} USDT — шум округления)${marginTxt}`
        : `Курсы в норме · профит положительный${marginTxt}`,
    });
  }
  return out;
}
